import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Grade, getGrades, MaterialProperties } from '@steelbuy/ts-shared';
import { FormItem, InputSelect } from '@steelbuy/ui-primitive';
import { GenericValidationProps } from '../validation/Validation';
import { useRequiredFieldValidator } from '../validation/ValidationHooks';

type MaterialGradeSelectionProps = {
    material: MaterialProperties;
    gradeSelected: (grade?: Grade) => void;
    getDefaultValue?: (material: MaterialProperties) => Grade | undefined;
} & GenericValidationProps;

export const MaterialGradeSelection = ({
    forceValidation,
    gradeSelected,
    material,
    required = false,
    getDefaultValue,
}: MaterialGradeSelectionProps) => {
    const { t } = useTranslation(['domainModel', 'uiDomain']);
    const requiredFieldValidator = useRequiredFieldValidator();

    const availableGrades = getGrades(material)?.slice().sort();
    const { grade } = material;

    useEffect(() => {
        const defaultValue = getDefaultValue?.(material);
        if (
            (grade !== undefined && !availableGrades.includes(grade)) ||
            (grade === undefined && defaultValue && availableGrades.includes(defaultValue))
        ) {
            gradeSelected(defaultValue);
        }

        if (material.grade !== undefined && !availableGrades.includes(material.grade)) {
            gradeSelected(undefined);
        }
    }, [material.grade, gradeSelected, availableGrades]);

    if (availableGrades.length === 0) {
        return null;
    }

    const materialGradeOptions = availableGrades.map((option) => ({
        label: t(`domainModel:material.grade.value.${option}`),
        value: option,
    }));

    return (
        <FormItem header={t('domainModel:material.grade.name')} isMandatory={required}>
            <InputSelect
                options={materialGradeOptions}
                placeholder={t('uiDomain:materialSelection.placeholder')}
                value={material.grade}
                onSelect={(value) => gradeSelected(value)}
                validators={required ? [requiredFieldValidator] : undefined}
                forceValidation={required ? forceValidation : undefined}
                testId="grade-select"
                searchable
            />
        </FormItem>
    );
};
